import React, { useContext, useEffect, useMemo } from 'react';
import {
  action,
  REPORT_DEU_CONSUMPTION_OPEN,
  REPORT_DEU_PLAN_CONSUMPTION_CHART_HOVER,
  REPORT_DEU_TOTAL_PLAN_TOOLTIP_HOVER,
  REPORT_DEU_WEEKLY_CONSUMPTION_CHART_HOVER,
} from '../../../../../shared/SegmentEvents';
import { InfoPaper } from '../../../components/ConsumptionData/InfoPaper';
import { ConsolidatedPaper } from '../../../components/ConsumptionData/ConsolidatedPaper';
import { LeftInfoPaperArea, RightInfoPaperArea } from '../../styles';
import { BillingMetrics } from '../../../../../shared/BillingMetrics';
import { useContentLocalizer } from '../../../../../hooks/useContentLocalizer';
import { localization } from './localization';
import { roundNextHundredth } from '../../../../../utils/numbers';
import DataConsumptionContext from '../../../../../contexts/Billing/DataConsumptionContext';
import DataConsumptionMetricContext from '../../../../../contexts/Billing/DataConsumptionMetricContext';
import { getOrdinal } from '../../../../../utils/strings';
import { toUTCDateString } from '../../../../../utils/date';
import { trackEvent } from '../../../../../api/Billing/Tracking';
import { useBillingPlanUsageContext } from '../../../../../contexts/Billing/BillingPlanUsageContext';
import colors from '../../../../../_charts.module.scss';

const DeuConsumptionComponent: React.FC = () => {
  const ROUND_UP_PERCENT = 1.25;

  const { dateToSearch, isMetricAllowed, tenant, loggedUser, trackingProps, periodInfo } = useContext(DataConsumptionContext);
  const { compiledDailyEngagedUserDataState, isDeuLoading, consolidatedDeuApiRequisition } =
    useContext(DataConsumptionMetricContext);
  const { planStatus } = useBillingPlanUsageContext();

  const consolidatedDeuConsumption = compiledDailyEngagedUserDataState.compiledDailyEngagedUserConsumption;
  const consolidatedContainsDataOnSelectedMonth = compiledDailyEngagedUserDataState.compiledContainsDataOnSelectedMonth;

  useEffect(() => {
    consolidatedDeuApiRequisition(dateToSearch, (metrics) =>
      metrics.some((metric) => isMetricAllowed(metric))
    );
  }, [dateToSearch]);

  const content = useContentLocalizer(localization);

  const getCustomColor = (metricId: BillingMetrics) => {
    return metricId === BillingMetrics.activeMessage ? colors.Brand : colors.System;
  };

  const getLeftChartLabel = (metricId: BillingMetrics) => {
    return metricId === BillingMetrics.activeMessage
      ? content.leftChart.labels.counted
      : content.leftChart.labels.available;
  };

  const arrWeekItems = consolidatedDeuConsumption.map((item) => item.weekItems);

  const arrLeftChartTotal = consolidatedDeuConsumption.map((item) => item.total);

  const arrLeftChartCustomLabel = consolidatedDeuConsumption.map((item) => ({
    backgroundColor: getCustomColor(item.metric.metricId),
    text: getLeftChartLabel(item.metric.metricId),
  }));

  const arrLeftChartLabel = consolidatedDeuConsumption.map((item) =>
    getLeftChartLabel(item.metric.metricId)
  );

  const arrLeftChartColor = arrLeftChartCustomLabel.map((item) => item.backgroundColor);

  const maxArrTotal = Math.max(...arrLeftChartTotal) > 0 ? Math.max(...arrLeftChartTotal) : 1;

  const leftChartData = {
    chartType: 'BarChart',
    title: content.leftChart.title,
    labels: arrLeftChartLabel,
    customLabels: arrLeftChartCustomLabel,
    datasets: [
      {
        data: arrLeftChartTotal,
        backgroundColor: arrLeftChartColor,
      },
    ],
  };

  const leftChartOptions = {
    maintainAspectRatio: false,
    barThickness: 15,
    locale: content.locale,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: { grid: { display: false, drawBorder: false } },
      y: consolidatedContainsDataOnSelectedMonth
        ? { min: 0, max: roundNextHundredth(maxArrTotal * ROUND_UP_PERCENT) }
        : { min: 0 },
    },
  };

  const weekItemsLabel = useMemo(() => {
    if (!arrWeekItems[0]) return [];
    return arrWeekItems[0].map((item) =>
      content.formatString(content.rightChart.period, {
        startDate: toUTCDateString(item.startDateReference, content.locale, false),
        endDate: toUTCDateString(item.endDateReference, content.locale, false),
      })
    );
  }, [arrWeekItems, content]);

  const arrRightChartCustomLabel = consolidatedDeuConsumption.map((item) => ({
    backgroundColor: getCustomColor(item.metric.metricId),
    text: getLeftChartLabel(item.metric.metricId),
  }));

  const arrRightChartDataset = consolidatedDeuConsumption.map((item) => ({
    label: getLeftChartLabel(item.metric.metricId),
    data: item.weekItems.map((week) => week.total),
    backgroundColor: getCustomColor(item.metric.metricId),
  }));

  const rightChartData = {
    chartType: 'BarChart',
    title: content.rightChart.title,
    labels: weekItemsLabel,
    customLabels: arrRightChartCustomLabel,
    datasets: arrRightChartDataset,
  };

  const arrWeekTotal = arrWeekItems.map((arrItem) => arrItem.map((item) => item.total)).flat(1);
  const maxArrWeekTotal = Math.max(...arrWeekTotal) > 0 ? Math.max(...arrWeekTotal) : 1;

  const rightChartOptions = {
    maintainAspectRatio: false,
    barThickness: 8,
    locale: content.locale,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: { grid: { display: false, drawBorder: false } },
      y: consolidatedContainsDataOnSelectedMonth
        ? { min: 0, max: roundNextHundredth(maxArrWeekTotal * ROUND_UP_PERCENT) }
        : { min: 0 },
    },
  };

  const header = {
    title: content.header.title,
    period: periodInfo,
    subtitle: content.header.description,
    learnMore: content.header.learnMore,
    modalContent: content.header.modalContent,
    buttonTitle: content.header.showMoreButton,
    pathname: '/deu-consumption',
    buttonTrackEvent: () => trackEvent(REPORT_DEU_CONSUMPTION_OPEN, tenant, loggedUser, trackingProps),
  };

  const metricDEU = consolidatedDeuConsumption.find(item => item.metric.metricId === BillingMetrics.dailyEngagedUser);
  const metricAM = consolidatedDeuConsumption.find(item => item.metric.metricId === BillingMetrics.activeMessage);

  return (
    <ConsolidatedPaper
      header={header}
      leftChart={{ leftChartData, leftChartOptions, redraw: false }}
      rightChart={{ rightChartData, rightChartOptions, redraw: false }}
      isLoading={isDeuLoading}
      leftInfoPapers={
        <LeftInfoPaperArea>
          <InfoPaper
            justifyContent={'space-between'}
            classNameTitle={'flex-start-margin'}
            icon={'user-engaged'}
            tooltipIcon={'info'}
            tooltipText={content.header.tooltipText}
            title={consolidatedContainsDataOnSelectedMonth ? (metricDEU?.total.toLocaleString(content.locale) || '0') : '--'}
            text={content.leftInfoPaper.totalInfo.description}
          />
          <InfoPaper
            justifyContent={'flex-start'}
            classNameTitle={'flex-start-margin'}
            icon={'user-default'}
            title={consolidatedContainsDataOnSelectedMonth ? (metricAM?.total.toLocaleString(content.locale) || '0') : '--'}
            text={content.leftInfoPaper.availableInfo.description}
          />
          <InfoPaper
            justifyContent={'flex-start'}
            classNameTitle={'flex-start-margin'}
            title={consolidatedContainsDataOnSelectedMonth ?
              content.formatString(content.rightInfoPaper.topWeekNumber.label, getOrdinal(metricAM?.topWeekNumber.toLocaleString(content.locale) || 0, content.locale, 'f'))
              : '--'}
            text={content.leftInfoPaper.planInfo.description}
          />
        </LeftInfoPaperArea>
      }
      rightInfoPapers={
        <RightInfoPaperArea>
          <InfoPaper
            justifyContent={'flex-start'}
            classNameTitle={'flex-start-margin'}
            icon={'user-engaged'}
            title={consolidatedContainsDataOnSelectedMonth ? (metricDEU?.weeklyAverage.toLocaleString(content.locale) || '0') : '--'}
            text={content.rightInfoPaper.weeklyAverage.description}
            bdsPaper={false}
          />
          <InfoPaper
            justifyContent={'flex-start'}
            classNameTitle={'flex-start-margin'}
            icon={'message-talk'}
            title={consolidatedContainsDataOnSelectedMonth ? (metricAM?.weeklyAverage.toLocaleString(content.locale) || '0') : '--'}
            text={content.rightInfoPaper.weeklyMsgAverage.description}
            bdsPaper={false}
          />
          <InfoPaper
            justifyContent={'flex-start'}
            icon={'calendar'}
            classNameTitle={'flex-start-margin'}
            title={consolidatedContainsDataOnSelectedMonth ?
              content.formatString(content.rightInfoPaper.topWeekNumber.label, getOrdinal(metricDEU?.topWeekNumber || 0, content.locale, 'f'))
              : '--'}
            text={content.rightInfoPaper.topWeekNumber.description}
            infoPaperType={'weekly'}
            bdsPaper={false}
          />
        </RightInfoPaperArea>
      }
    />
  );
};

export default DeuConsumptionComponent;